import { useMemo } from 'react'
import { VStack, Text } from '@chakra-ui/react'
import { useTranslation, Trans } from 'next-i18next'

import { logEvent } from '@/lib/gtm'
import env from '@/lib/env'

import { TEXT_STYLE } from '@/features/landing/constants/FAQs'
import { Item as FAQItem } from '@/features/landing/components/FAQs'
import type { HeroProps } from '@/features/services/templates/Hero'
import type { ServiceInfoProps } from '@/features/services/templates/ServiceInfo'
import type { TextWithImageProps } from '@/features/services/templates/TextWithImage'
import { strongStyle } from '@/features/landing/components/Carousel/Contents/style'

import DesktopBgImage from '@/images/services/advisory/banner-desktop.jpg'
import MobileBgImage from '@/images/services/advisory/banner-mobile.jpg'
import InfoImage from '@/images/services/advisory/info.png'
import FloatImage from '@/images/services/advisory/floating.jpg'

import type { WidgetProps, IconTextProps } from '../types'
import { TARGET_TEXT_STYLE } from './style'

export const useMeta = (): PageMeta => {
  const { t } = useTranslation('services')

  return {
    title: t('advisory.meta.title'),
    description: t('advisory.meta.description'),
    og: {
      title: t('advisory.meta.og.title'),
      description: t('advisory.meta.og.description'),
      image: {
        url: `${env.APP_URL}${t('advisory.meta.og.image.url')}`,
      },
    },
  }
}

export const useHeroData = (): HeroProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('advisory.title'),
      subtitle: t('advisory.subtitle'),
      image: {
        base: `url(${MobileBgImage.src})`,
        md: `url(${DesktopBgImage.src})`,
      },
      titleColor: 'white',
      subtitleColor: 'white',
      colorSchemePrimary: 'primary',
      colorSchemeSecondary: 'white',
      bgColor: 'secondary.800',
      onClickGetQuote: (): void => {
        logEvent({
          ga: {
            category: 'Advisory',
            action: 'Click get quote',
          },
        })
      },
      onClickGetHandout: (): void => {
        logEvent({
          ga: {
            category: 'Advisory',
            action: 'Click download handout',
            label: 'header',
          },
        })
      },
    }),
    [language],
  )
}

export const useServiceInfoData = (): ServiceInfoProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('advisory.serviceInfo.title'),
      paragraph: t('advisory.serviceInfo.paragraph'),
      image: InfoImage,
    }),
    [language],
  )
}

export const useAreasData = (): IconTextProps[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => [
      {
        image: '/images/services/advisory/area-1.svg',
        text: t('advisory.areas.contents.0'),
      },
      {
        image: '/images/services/advisory/area-2.svg',
        text: t('advisory.areas.contents.1'),
      },
      {
        image: '/images/services/advisory/area-3.svg',
        text: t('advisory.areas.contents.2'),
      },
      {
        image: '/images/services/advisory/area-4.svg',
        text: t('advisory.areas.contents.3'),
      },
      {
        image: '/images/services/advisory/area-5.svg',
        text: t('advisory.areas.contents.4'),
      },
      {
        image: '/images/services/advisory/area-6.svg',
        text: t('advisory.areas.contents.5'),
      },
      {
        image: '/images/services/advisory/area-7.svg',
        text: t('advisory.areas.contents.6'),
      },
      {
        image: '/images/services/advisory/area-8.svg',
        text: t('advisory.areas.contents.7'),
      },
    ],
    [language],
  )
}

export const useTargetData = (): TextWithImageProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('advisory.target.title'),
      paragraph: (
        <Text sx={TARGET_TEXT_STYLE}>{t('advisory.target.paragraph')}</Text>
      ),
      image: FloatImage,
      imageRatio: 452 / 242,
    }),
    [language],
  )
}

export const useFAQsData = (): FAQItem[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('faqs')

  return useMemo(
    () => [
      {
        title: t('FAQs.3.question'),
        content: (
          <VStack spacing="4" sx={{ alignItems: 'stretch' }}>
            <Text sx={TEXT_STYLE}>{t('FAQs.3.answer.paragraph.0')}</Text>
            <Text sx={TEXT_STYLE}>{t('FAQs.3.answer.paragraph.1')}</Text>
          </VStack>
        ),
      },
      {
        title: t('FAQs.4.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.4.answer.paragraph')}</Text>,
      },
      {
        title: t('FAQs.5.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.5.answer.paragraph')}</Text>,
      },
    ],
    [language],
  )
}

export const useWhatYouWillGetData = (): WidgetProps[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => [
      {
        image: '/images/services/advisory/what-you-will-get-1.svg',
        text: (
          <Trans t={t} i18nKey="advisory.whatYouWillGet.contents.0">
            ความเข้าใจและแนวทางในการปฏิบัติตาม
            <Text as="strong" sx={strongStyle}>
              PDPA
            </Text>
            อย่างละเอียด
          </Trans>
        ),
      },
    ],
    [language],
  )
}

export const usePricingData = (): React.ReactNode => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => (
      <Trans t={t} i18nKey="advisory.pricing.contents.0">
        เริ่มต้นที่
        <Text as="strong" sx={strongStyle}>
          19,000 บาท
        </Text>
        ต่อการปรึกษาจำนวน
        <Text as="strong" sx={strongStyle}>
          5 ชั่วโมง ภายใน 1 เดือน
        </Text>
      </Trans>
    ),
    [language],
  )
}

export const useWhyUsData = (): React.ReactNode[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => [
      <Trans key="1" t={t} i18nKey="advisory.whyUs.contents.0">
        ต้องการคำแนะนำโดยนักกฏหมายเพิ่มเติมหลังจากได้ดำเนินการทำให้ธุรกิจสอดคล้องกับ
        <Text as="strong" sx={strongStyle}>
          PDPA
        </Text>
        ไปแล้วบางส่วน
      </Trans>,
      <Trans key="2" t={t} i18nKey="advisory.whyUs.contents.1">
        ต้องการคำแนะนำเกี่ยวกับแนวปฏิบัติ
        <Text as="strong" sx={strongStyle}>
          PDPA
        </Text>
        จากผู้เชี่ยวชาญแล้วนำไปดำเนินการด้วยตนเอง
      </Trans>,
      <Trans key="3" t={t} i18nKey="advisory.whyUs.contents.2">
        ไม่แน่ใจว่าควรทำตามแนวปฏิบัติใดเพื่อให้แน่ใจว่าถูกต้องตามข้อกฎหมายในปัจจุบัน
      </Trans>,
    ],
    [language],
  )
}
